<template>
  <div class="njangi-app">
    <v-overlay :value="loading.initial" z-index="999" opacity="0.8">
      <v-progress-circular indeterminate size="64" color="primary" width="4" />
      <div class="mt-4 text-subtitle-1 font-weight-bold white--text">Loading...</div>
    </v-overlay>

    <v-fade-transition>
      <div v-if="!loading.initial">
        <!-- Stats -->
        <v-row class="mb-4 mb-sm-6">
          <v-col v-for="stat in quickStats" :key="stat.title" cols="6" sm="3">
            <v-card class="rounded-xl h-100 stat-card" elevation="2" outlined>
              <v-card-text class="pa-3 pa-sm-4 text-center">
                <v-icon :color="stat.color" size="32" class="mb-1 mb-sm-2">{{ stat.icon }}</v-icon>
                <div class="text-h6 text-sm-h5 font-weight-bold">{{ stat.value }}</div>
                <div class="text-caption grey--text text-uppercase">{{ stat.title }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Filters -->
        <v-card class="mb-4 mb-sm-6 rounded-xl" elevation="2" outlined>
          <v-card-text class="pa-3 pa-sm-4">
            <v-row dense>
              <v-col cols="12" sm="6" md="4" class="pb-2 pb-sm-0">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                  label="Search..."
                  outlined
                  dense
                  clearable
                  hide-details
                  class="rounded-lg"
                />
              </v-col>
              <v-col cols="6" sm="3" md="2" class="pr-1 pr-sm-2">
                <v-select
                  v-model="filters.status"
                  :items="statusItems"
                  label="Status"
                  outlined
                  dense
                  clearable
                  hide-details
                  class="rounded-lg"
                />
              </v-col>
              <v-col cols="6" sm="3" md="2" class="pl-1 pl-sm-2">
                <v-select
                  v-model="filters.cycle"
                  :items="cycleItems"
                  label="Cycle"
                  outlined
                  dense
                  clearable
                  hide-details
                  class="rounded-lg"
                />
              </v-col>
              <v-col cols="12" md="4" class="pt-2 pt-md-0 d-flex">
                <v-btn color="primary" depressed class="mr-2 rounded-lg flex-grow-1" small @click="applyFilters">
                  <v-icon left x-small>mdi-filter</v-icon> <span class="d-none d-sm-inline">Filter</span>
                </v-btn>
                <v-btn outlined class="rounded-lg flex-grow-1" small @click="resetFilters">
                  <v-icon left x-small>mdi-refresh</v-icon> <span class="d-none d-sm-inline">Reset</span>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Groups Table -->
        <v-card class="rounded-xl" elevation="3" outlined>
          <v-card-title class="d-flex flex-wrap align-center py-3 px-3 px-sm-6">
            <span class="text-h6 font-weight-bold mr-auto">Njangi Groups</span>
            <div class="d-flex mt-2 mt-sm-0">
              <v-btn
                color="primary"
                depressed
                class="mr-2 rounded-lg"
                x-small
                :to="{ name: 'njangi.create_group' }"
              >
                <v-icon left x-small>mdi-plus</v-icon> <span class="d-none d-sm-inline">New</span>
              </v-btn>
              <v-btn outlined class="rounded-lg" x-small @click="exportData">
                <v-icon left x-small>mdi-export</v-icon> <span class="d-none d-sm-inline">Export</span>
              </v-btn>
            </div>
          </v-card-title>

          <v-divider />

          <v-progress-linear v-if="loading.table" indeterminate color="primary" height="3" />

          <v-data-table
            :headers="headers"
            :items="filteredGroups"
            :search="search"
            :loading="loading.table"
            :items-per-page="pagination.itemsPerPage"
            :page.sync="pagination.page"
            :server-items-length="totalGroups"
            :mobile-breakpoint="768"
            show-select
            item-key="id"
            class="elevation-0"
            hide-default-footer
            @click:row="viewGroupDetails"
          >
            <template v-slot:item.name="{ item }">
              <div class="d-flex align-center py-1 py-sm-2">
                <v-avatar color="primary" size="32" class="mr-2 mr-sm-3">
                  <span class="white--text font-weight-bold text-body-2">{{ item.name.charAt(0) }}</span>
                </v-avatar>
                <div class="min-width-0">
                  <div class="font-weight-bold text-body-2 text-sm-body-1 text-truncate">{{ item.name }}</div>
                  <div class="text-caption grey--text text-truncate">{{ item.admin }}</div>
                </div>
              </div>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip
                :color="item.is_active ? 'success' : 'grey'"
                :outlined="!item.is_active"
                x-small
                dark
                class="font-weight-medium"
              >
                {{ item.status }}
              </v-chip>
            </template>

            <template v-slot:item.cycle="{ item }">
              <v-chip :color="getCycleColor(item.cycle)" x-small outlined class="font-weight-medium">
                <v-icon left x-small>{{ getCycleIcon(item.cycle) }}</v-icon>
                <span class="d-none d-sm-inline">{{ item.cycle }}</span>
              </v-chip>
            </template>

            <template v-slot:item.members="{ item }">
              <div class="text-caption text-sm-body-2 font-weight-medium">
                {{ item.active_members }}/{{ item.total_members }}
              </div>
              <v-progress-linear
                :value="getMemberProgress(item)"
                height="4"
                rounded
                :color="getProgressColor(getMemberProgress(item))"
                class="mt-1"
                style="max-width: 80px"
              />
            </template>

            <template v-slot:item.contribution_rate="{ item }">
              <div class="d-flex align-center">
                <v-progress-circular
                  :value="item.contribution_rate"
                  :color="getProgressColor(item.contribution_rate)"
                  size="28"
                  width="2"
                  rotate="-90"
                  class="mr-1 mr-sm-2"
                >
                  <span class="text-caption font-weight-bold">{{ item.contribution_rate }}%</span>
                </v-progress-circular>
                <div class="text-caption grey--text d-none d-sm-block">
                  {{ formatCurrency(item.collected_amount) }}
                </div>
              </div>
            </template>

            <template v-slot:item.penalties="{ item }">
              <v-chip
                v-if="item.penalties_count > 0"
                color="warning"
                x-small
                dark
                class="font-weight-medium"
                @click.stop="viewPenalties(item)"
              >
                {{ item.penalties_count }}
                <span class="d-none d-sm-inline">({{ formatCurrency(item.penalties_amount) }})</span>
              </v-chip>
              <span v-else class="text-caption grey--text">—</span>
            </template>

            <template v-slot:item.last_activity="{ item }">
              <span class="text-caption">{{ formatDate(item.last_activity) || 'Never' }}</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="d-flex">
                <v-btn icon x-small color="primary" class="mr-1" @click.stop="viewGroupDetails(item)">
                  <v-icon x-small>mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  :color="item.is_active ? 'warning' : 'success'"
                  @click.stop="toggleStatus(item)"
                >
                  <v-icon x-small>{{ item.is_active ? 'mdi-pause' : 'mdi-play' }}</v-icon>
                </v-btn>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="pa-6 pa-sm-8 text-center">
                <v-icon size="48" color="grey lighten-2">mdi-account-group-outline</v-icon>
                <div class="text-subtitle-1 grey--text mt-3">No group found</div>
                <v-btn color="primary" text class="mt-2" small @click="resetFilters">Reset</v-btn>
              </div>
            </template>
          </v-data-table>

          <v-divider />
          <v-card-actions class="px-3 px-sm-6 py-2 py-sm-3 flex-wrap">
            <span class="text-caption grey--text mb-2 mb-sm-0">{{ paginationInfo }}</span>
            <v-spacer class="d-none d-sm-block" />
            <div class="d-flex align-center">
              <v-select
                v-model="pagination.itemsPerPage"
                :items="[10, 20, 50]"
                label="Rows"
                outlined
                dense
                hide-details
                style="max-width: 80px"
                class="mr-2 mr-sm-4"
              />
              <v-pagination
                v-model="pagination.page"
                :length="paginationLength"
                :total-visible="3"
                circle
                color="primary"
                :size="28"
              />
            </div>
          </v-card-actions>
        </v-card>

        <!-- Performance Footer -->
        <v-card class="rounded-xl mt-4 mt-sm-6" elevation="1" outlined>
          <v-card-text class="pa-3 pa-sm-4">
            <v-row class="text-center" dense>
              <v-col v-for="stat in performanceStats" :key="stat.title" cols="6" md="3">
                <div class="text-h6 text-sm-h5 font-weight-bold" :class="stat.color + '--text'">{{ stat.value }}</div>
                <div class="text-caption grey--text text-uppercase">{{ stat.title }}</div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </div>
    </v-fade-transition>

    <!-- Details Dialog -->
    <v-dialog
      v-model="dialogs.details"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :max-width="$vuetify.breakpoint.xsOnly ? undefined : '1200px'"
      scrollable
      persistent
    >
      <v-card v-if="selectedGroup" class="rounded-xl" :class="{ 'rounded-0': $vuetify.breakpoint.xsOnly }">
        <v-card-title class="primary white--text py-3 px-3 px-sm-6">
          <v-icon left small>mdi-information-outline</v-icon>
          <span class="text-subtitle-1 text-sm-h6 font-weight-bold text-truncate">{{ selectedGroup.name }}</span>
          <v-spacer />
          <v-btn icon dark small @click="dialogs.details = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-tabs v-model="detailsTab" grow color="primary" show-arrows class="px-2 px-sm-4">
          <v-tab class="text-caption text-sm-body-2">Overview</v-tab>
          <v-tab class="text-caption text-sm-body-2">Members</v-tab>
          <v-tab class="text-caption text-sm-body-2">Transactions</v-tab>
          <v-tab class="text-caption text-sm-body-2">Period</v-tab>
        </v-tabs>

        <v-tabs-items v-model="detailsTab">
          <!-- Overview Tab -->
          <v-tab-item class="pa-3 pa-sm-4">
            <v-row dense>
              <v-col cols="12" md="8">
                <v-card outlined class="mb-3 mb-sm-4 rounded-xl">
                  <v-card-title class="subtitle-2 subtitle-1 font-weight-bold">Information</v-card-title>
                  <v-card-text class="pa-2 pa-sm-4">
                    <v-list dense>
                      <v-list-item class="px-0">
                        <v-list-item-content>
                          <v-list-item-subtitle>Administrator</v-list-item-subtitle>
                          <v-list-item-title>{{ selectedGroup.admin }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item class="px-0">
                        <v-list-item-content>
                          <v-list-item-subtitle>Contact</v-list-item-subtitle>
                          <v-list-item-title>{{ selectedGroup.njangi_contact || 'N/A' }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item class="px-0">
                        <v-list-item-content>
                          <v-list-item-subtitle>Created</v-list-item-subtitle>
                          <v-list-item-title>{{ formatDate(selectedGroup.created_at) }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item class="px-0">
                        <v-list-item-content>
                          <v-list-item-subtitle>Contribution</v-list-item-subtitle>
                          <v-list-item-title>
                            {{ formatCurrency(selectedGroup.contribution_amount) }} / {{ selectedGroup.cycle }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item class="px-0">
                        <v-list-item-content>
                          <v-list-item-subtitle>Next Collection</v-list-item-subtitle>
                          <v-list-item-title>
                            {{ formatDate(selectedGroup.setting_details && selectedGroup.setting_details.next_collection_date) || 'N/A' }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>

                <v-card outlined class="rounded-xl">
                  <v-card-title class="subtitle-2 subtitle-1 font-weight-bold">Financial Summary</v-card-title>
                  <v-card-text class="pa-2 pa-sm-4">
                    <v-row dense class="text-center mb-3 mb-sm-4">
                      <v-col v-for="f in financialStats" :key="f.title" cols="6" sm="3">
                        <div class="text-subtitle-1 text-sm-h6 font-weight-bold" :class="f.color + '--text'">{{ f.value }}</div>
                        <div class="text-caption grey--text">{{ f.title }}</div>
                      </v-col>
                    </v-row>
                    <v-progress-linear
                      :value="selectedGroup.contribution_rate"
                      height="20"
                      color="primary"
                      rounded
                      class="font-weight-bold white--text"
                    >
                      {{ Math.ceil(selectedGroup.contribution_rate) }}% collected
                    </v-progress-linear>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="4">
                <v-card outlined class="mb-3 mb-sm-4 rounded-xl">
                  <v-card-title class="subtitle-2 subtitle-1 font-weight-bold">Status</v-card-title>
                  <v-card-text class="pa-2 pa-sm-4">
                    <v-chip
                      v-if="selectedGroup.current_cycle"
                      small
                      color="primary"
                      dark
                      class="mb-3"
                    >
                      <v-icon left x-small>mdi-recycle</v-icon>
                      Cycle {{ selectedGroup.current_cycle }}
                    </v-chip>
                    <v-chip
                      :color="selectedGroup.is_active ? 'success' : 'grey'"
                      small
                      dark
                      class="mb-3"
                    >
                      {{ selectedGroup.status }}
                    </v-chip>
                    <v-list dense>
                      <v-list-item class="px-0">
                        <v-list-item-content>
                          <v-list-item-subtitle>Active Collection</v-list-item-subtitle>
                          <v-list-item-title>{{ selectedGroup.is_active ? 'Yes' : 'No' }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item class="px-0">
                        <v-list-item-content>
                          <v-list-item-subtitle>Members</v-list-item-subtitle>
                          <v-list-item-title>{{ selectedGroup.active_members }}/{{ selectedGroup.total_members }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>

                <v-card outlined class="rounded-xl">
                  <v-card-title class="subtitle-2 subtitle-1 font-weight-bold">Actions</v-card-title>
                  <v-card-text class="pa-2 pa-sm-4">
                    <v-btn block color="primary" class="mb-2 rounded-lg" small @click="detailsTab = 1">
                      <v-icon left x-small>mdi-account-group</v-icon> Members
                    </v-btn>
                    <v-btn block color="warning" class="mb-2 rounded-lg" dark small @click="sendReminders(selectedGroup)">
                      <v-icon left x-small>mdi-bell</v-icon> Reminders
                    </v-btn>
                    <v-btn block color="info" class="rounded-lg" dark small @click="generateReport">
                      <v-icon left x-small>mdi-file-chart</v-icon> Report
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Members Tab -->
          <v-tab-item class="pa-3 pa-sm-4">
            <div style="max-height: 55vh; overflow-y: auto;">
              <v-data-table
                :headers="memberHeaders"
                :items="groupMembers"
                :items-per-page="100"
                :mobile-breakpoint="600"
                class="elevation-0"
                hide-default-footer
              >
                <template v-slot:no-data>
                  <div class="pa-6 text-center grey--text">No member found</div>
                </template>
              </v-data-table>
            </div>
          </v-tab-item>

          <!-- Transactions Tab – Affichage groupé par date -->
          <v-tab-item class="pa-3 pa-sm-4">
            <div style="max-height: 55vh; overflow-y: auto;">
              <v-card
                v-for="(day, i) in transactionsByDate"
                :key="i"
                class="mb-3 rounded-lg elevation-1"
                outlined
              >
                <v-card-title class="py-2 px-3 subtitle-2 font-weight-bold primary--text">
                  <v-icon left small color="primary">mdi-calendar</v-icon>
                  {{ day.date }}
                  <v-spacer />
                  <v-chip x-small label>{{ day.transactions.length }} transaction(s)</v-chip>
                </v-card-title>
                <v-divider />
                <v-list dense class="py-0">
                  <v-list-item
                    v-for="tx in day.transactions"
                    :key="tx.id || tx.date + tx.member"
                    class="px-3"
                  >
                    <v-list-item-avatar>
                      <v-icon :color="tx.type === 'Dépôt' ? 'success' : tx.type === 'Retrait' ? 'error' : 'warning'">
                        {{ tx.type === 'Dépôt' ? 'mdi-arrow-down-bold' : tx.type === 'Retrait' ? 'mdi-arrow-up-bold' : 'mdi-alert' }}
                      </v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2">
                        <strong>{{ tx.member }}</strong>
                        <span class="grey--text text-caption ml-2">{{ tx.member_phone }}</span>
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ tx.type }} • {{ tx.amount }}
                        <v-chip
                          x-small
                          :color="tx.status === 'Confirmé' ? 'success' : tx.status === 'En attente' ? 'warning' : 'grey'"
                          text-color="white"
                          class="ml-2"
                        >
                          {{ tx.status }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
              <div v-if="!transactionsByDate.length" class="pa-6 text-center grey--text">
                <v-icon size="48" color="grey lighten-2">mdi-cash-remove</v-icon>
                <div class="mt-3">No transaction</div>
              </div>
            </div>
          </v-tab-item>

          <!-- Period Tab -->
          <v-tab-item class="pa-3 pa-sm-4">
            <PeriodView
              :group="selectedGroup"
              :getNjangiWeekNumber="getNjangiWeekNumber"
              :getMondayOfWeek="getMondayOfWeek"
              :getCurrentWeek="getCurrentWeek"
              :formatWeekString="formatWeekString"
            />
          </v-tab-item>
        </v-tabs-items>

        <v-divider />
        <v-card-actions class="px-3 px-sm-6 py-3 py-sm-4">
          <v-spacer />
          <v-btn color="grey" text class="rounded-lg" small @click="dialogs.details = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top rounded="lg">
      <v-icon left color="white" small>{{ snackbar.icon }}</v-icon>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false" color="white" small>Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'
import PeriodView from './PeriodView.vue'

export default {
  name: 'NjangiManagement',
  components: { PeriodView },

  data() {
    return {
      loading: { initial: true, table: false, action: false },
      groups: [],
      totalGroups: 0,
      search: '',
      filters: { status: null, cycle: null },
      statusItems: ['Active', 'Inactive'],
      cycleItems: ['Daily', 'Weekly', 'Monthly', 'Custom'],
      pagination: { page: 1, itemsPerPage: 20 },
      quickStats: [
        { title: 'Groups', value: 0, icon: 'mdi-account-group', color: 'primary' },
        { title: 'Members', value: 0, icon: 'mdi-account-check', color: 'success' },
        { title: 'Collected', value: '0 FCFA', icon: 'mdi-cash-multiple', color: 'green' },
        { title: 'Penalties', value: 0, icon: 'mdi-alert-circle', color: 'warning' }
      ],
      performanceStats: [
        { title: 'Collection', value: '0%', color: 'primary' },
        { title: 'Active', value: '0%', color: 'success' },
        { title: 'Late', value: '0%', color: 'warning' },
        { title: 'Satisfaction', value: '0%', color: 'info' }
      ],
      headers: [
        { text: 'Group', value: 'name', width: '25%' },
        { text: 'Status', value: 'status', width: '10%' },
        { text: 'Cycle', value: 'cycle', width: '10%' },
        { text: 'Members', value: 'members', width: '10%' },
        { text: 'Collected', value: 'contribution_rate', width: '15%' },
        { text: 'Penalties', value: 'penalties', width: '12%' },
        { text: 'Activity', value: 'last_activity', width: '10%' },
        { text: 'Actions', value: 'actions', sortable: false, width: '10%', align: 'center' }
      ],
      dialogs: { details: false, form: false },
      detailsTab: 0,
      selectedGroup: null,
      editingGroup: null,
      form: { name: '', contact: '', cycle: 'Monthly', contribution_amount: 5000, valid: false },
      groupMembers: [],
      groupTransactions: [],
      memberHeaders: [
        { text: 'Name', value: 'name' },
        { text: 'Phone', value: 'phone' },
        { text: 'Email', value: 'email' },
        { text: 'Position', value: 'position' },
        { text: 'Status', value: 'status' },
        { text: 'Last contrib.', value: 'last_contribution' }
      ],
      transactionHeaders: [
        { text: 'Date', value: 'date' },
        { text: 'Member', value: 'member' },
        { text: 'Email', value: 'member_email' },
        { text: 'Phone', value: 'member_phone' },
        { text: 'Amount', value: 'amount' },
        { text: 'Type', value: 'type' },
        { text: 'Status', value: 'status' }
      ],
      snackbar: { show: false, message: '', color: 'success', icon: 'mdi-check-circle' }
    }
  },

  computed: {
    filteredGroups() {
      let result = [...this.groups]
      if (this.filters.status) result = result.filter(g => g.status === this.filters.status)
      if (this.filters.cycle) result = result.filter(g => g.cycle === this.filters.cycle)
      return result
    },
    paginationLength() {
      return Math.ceil(this.totalGroups / this.pagination.itemsPerPage) || 1
    },
    paginationInfo() {
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage + 1
      const end = Math.min(this.pagination.page * this.pagination.itemsPerPage, this.totalGroups)
      return 'Showing ' + start + ' to ' + end + ' of ' + this.totalGroups + ' groups'
    },
    financialStats() {
      if (!this.selectedGroup) return []
      return [
        { title: 'Collected', value: this.formatCurrency(this.selectedGroup.collected_amount), color: 'success' },
        { title: 'Expected', value: this.formatCurrency(this.selectedGroup.expected_amount), color: 'primary' },
        { title: 'Penalties', value: this.formatCurrency(this.selectedGroup.penalties_amount), color: 'warning' },
        { title: 'Balance', value: this.formatCurrency(this.selectedGroup.collected_amount - this.selectedGroup.penalties_amount), color: 'info' }
      ]
    },
    transactionsByDate() {
      if (!this.groupTransactions.length) return []
      const groups = {}
      this.groupTransactions.forEach(t => {
        const day = t.date ? t.date.split(' ').slice(0, 4).join(' ') : 'Unknown'
        if (!groups[day]) groups[day] = []
        groups[day].push(t)
      })
      return Object.entries(groups)
        .map(([date, transactions]) => ({ date, transactions }))
        .sort((a, b) => new Date(b.transactions[0].date) - new Date(a.transactions[0].date))
    }
  },

  mounted() {
    this.initializeData()
  },

  methods: {
    getNjangiWeekNumber(date) {
      const d = new Date(date)
      d.setHours(0, 0, 0, 0)
      const dayOfWeek = d.getDay()
      if (dayOfWeek === 1) {
        d.setDate(d.getDate() - 1)
      }
      d.setDate(d.getDate() + 4 - (d.getDay() || 7))
      const yearStart = new Date(d.getFullYear(), 0, 1)
      const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
      return weekNo
    },
    getMondayOfWeek(year, week) {
      const jan4 = new Date(year, 0, 4)
      const jan4Day = jan4.getDay() || 7
      const mondayISOWeek1 = new Date(jan4)
      mondayISOWeek1.setDate(jan4.getDate() - (jan4Day - 1))
      const targetMonday = new Date(mondayISOWeek1)
      targetMonday.setDate(mondayISOWeek1.getDate() + (week - 1) * 7)
      return targetMonday
    },
    getCurrentWeek() {
      const now = new Date()
      const week = this.getNjangiWeekNumber(now)
      return `${now.getFullYear()}-W${week.toString().padStart(2, '0')}`
    },
    formatWeekString(weekStr) {
      try {
        const [year, week] = weekStr.split('-W')
        if (!year || !week) return 'Select a week'
        const monday = this.getMondayOfWeek(parseInt(year), parseInt(week))
        const tuesday = new Date(monday)
        tuesday.setDate(monday.getDate() + 1)
        const mondayEnd = new Date(tuesday)
        mondayEnd.setDate(tuesday.getDate() + 6)
        const isCurrentWeek = weekStr === this.getCurrentWeek()
        const weekLabel = isCurrentWeek ? `📅 Current Week ${week}` : `Week ${week}`
        const opts = { month: 'short', day: 'numeric' }
        return `${weekLabel} (${year}): ${tuesday.toLocaleDateString('en-US', opts)} → ${mondayEnd.toLocaleDateString('en-US', opts)}`
      } catch (e) {
        return 'Select a week'
      }
    },

    async initializeData() {
      this.loading.initial = true
      try {
        await Promise.all([this.loadGroups(), this.loadStatistics()])
      } catch (error) {
        this.showError('Error loading initial data')
      } finally {
        this.loading.initial = false
      }
    },

    async loadGroups() {
      this.loading.table = true
      try {
        const res = await axios.get('/api/v1/manager/njangi/list/groups/null/null/')
        this.groups = res.data || []
        this.totalGroups = this.groups.length
      } catch (error) {
        this.showError('Failed to load groups')
      } finally {
        this.loading.table = false
      }
    },

    async loadStatistics() {
      try {
        const res = await axios.get('/api/v1/manager/njangi/statistics/')
        if (!res.data || !res.data.success) return
        const s = res.data.data
        this.quickStats[0].value = s.total_groups || 0
        this.quickStats[1].value = s.total_members || 0
        this.quickStats[2].value = this.formatCurrency(s.total_collected)
        this.quickStats[3].value = (s.total_penalties || 0) - (s.paid_penalties || 0)
        this.performanceStats[0].value = Math.round(s.collection_rate || 0) + '%'
        this.performanceStats[1].value = Math.round((s.active_groups / s.total_groups * 100) || 0) + '%'
        this.performanceStats[2].value = Math.round(s.overdue_groups || 0) + '%'
      } catch (error) {
        console.error('Statistics error:', error)
      }
    },

    async toggleStatus(group) {
      try {
        const endpoint = group.is_active
          ? '/api/v1/manager/njangi/deactivate/' + group.id + '/'
          : '/api/v1/manager/njangi/activate/' + group.id + '/'
        const res = await axios.post(endpoint)
        const message = (res.data && res.data[0] && res.data[0].text) ? res.data[0].text : 'Status updated'
        group.is_active = !group.is_active
        group.status = group.is_active ? 'Active' : 'Inactive'
        this.showSuccess(message)
        this.loadStatistics()
      } catch (error) {
        this.showError('Failed to change status')
      }
    },

    async sendReminders(group) {
      try {
        const res = await axios.post('/api/v1/manager/njangi/groups/' + group.id + '/send-reminders/')
        this.showSuccess((res.data && res.data.message) ? res.data.message : 'Reminders sent')
      } catch (error) {
        this.showError('Failed to send reminders')
      }
    },

    async viewGroupDetails(group) {
      this.selectedGroup = group
      this.detailsTab = 0
      this.dialogs.details = true
      await this.loadGroupDetails(group.id)
    },

    async loadGroupDetails(groupId) {
      try {
        const settingsRes = await axios.get('/api/v1/njangi/get/user/group/setting/detail/' + groupId + '/')
        if (settingsRes.data && settingsRes.data[1]) {
          this.selectedGroup = { ...this.selectedGroup, ...settingsRes.data[1] }
        }

        const membersRes = await axios.get('/api/v1/manager/njangi/group/' + groupId + '/member-positions/')
        if (membersRes.data) {
          this.groupMembers = membersRes.data.map(m => ({
            name: m.member_name,
            phone: m.phone || '—',
            email: m.email || '—',
            position: m.position,
            status: m.status || 'Ongoing',
            last_contribution: m.last_contribution || '—'
          }))
          console.log('Members loaded:', this.groupMembers.length)
        } else {
          this.groupMembers = []
        }

        const transRes = await axios.get('/api/v1/manager/njangi/group/' + groupId + '/transactions-enriched/')
        if (transRes.data) {
          this.groupTransactions = transRes.data.map(t => ({
            date: t.date,
            member: t.member,
            member_email: t.member_email,
            member_phone: t.member_phone,
            amount: this.formatCurrency(t.amount),
            type: t.type,
            status: t.status
          }))
          console.log('Transactions loaded:', this.groupTransactions.length)
        } else {
          this.groupTransactions = []
        }
      } catch (error) {
        console.error('Error loading group details:', error)
        this.groupMembers = []
        this.groupTransactions = []
        this.showError('Failed to load group details')
      }
    },

    viewPenalties(group) {
      this.selectedGroup = group
      this.detailsTab = 0
      this.dialogs.details = true
    },

    generateReport() {
      this.showInfo('Report generation started...')
    },

    applyFilters() {
      this.pagination.page = 1
    },

    resetFilters() {
      this.filters = { status: null, cycle: null }
      this.search = ''
      this.applyFilters()
    },

    exportData() {
      this.showInfo('Export under development')
    },

    formatCurrency(value) {
      if (!value && value !== 0) return '0 FCFA'
      return Number(value).toLocaleString() + ' FCFA'
    },

    formatDate(date) {
      if (!date) return null
      return new Date(date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
    },

    getCycleColor(cycle) {
      const colors = { Daily: 'blue', Weekly: 'teal', Monthly: 'orange', Custom: 'purple' }
      return colors[cycle] || 'grey'
    },

    getCycleIcon(cycle) {
      const icons = {
        Daily: 'mdi-calendar-today',
        Weekly: 'mdi-calendar-week',
        Monthly: 'mdi-calendar-month',
        Custom: 'mdi-cog-outline'
      }
      return icons[cycle] || 'mdi-calendar'
    },

    getProgressColor(value) {
      if (value >= 90) return 'success'
      if (value >= 70) return 'info'
      if (value >= 50) return 'warning'
      return 'error'
    },

    getMemberProgress(item) {
      if (!item.total_members) return 0
      return Math.round((item.active_members / item.total_members) * 100)
    },

    showSuccess(message) {
      this.snackbar = { show: true, message: message, color: 'success', icon: 'mdi-check-circle' }
    },

    showError(message) {
      this.snackbar = { show: true, message: message, color: 'error', icon: 'mdi-alert-circle' }
    },

    showInfo(message) {
      this.snackbar = { show: true, message: message, color: 'info', icon: 'mdi-information' }
    }
  }
}
</script>

<style scoped>
.njangi-app {
  padding: 8px;
  max-width: 1400px;
  margin: 0 auto;
  background-color: #f5f7fa;
  min-height: 100vh;
}

@media (min-width: 600px) {
  .njangi-app {
    padding: 16px;
  }
}

@media (min-width: 960px) {
  .njangi-app {
    padding: 24px;
  }
}

.stat-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.min-width-0 {
  min-width: 0;
}

::v-deep .v-data-table {
  border-radius: 0 !important;
}

::v-deep .v-data-table-header th {
  font-weight: 600 !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.6) !important;
  background-color: #fafafa;
  white-space: nowrap;
}

::v-deep .v-data-table tbody tr {
  transition: background-color 0.2s ease;
  cursor: pointer;
}

::v-deep .v-data-table tbody tr:hover {
  background-color: #f5f5f5 !important;
}

@media (max-width: 768px) {
  ::v-deep .v-data-table-header {
    display: none;
  }

  ::v-deep .v-data-table__mobile-table-row {
    display: flex;
    flex-direction: column;
    padding: 12px 16px;
    border-bottom: 1px solid #e0e0e0;
  }

  ::v-deep .v-data-table__mobile-row {
    min-height: auto;
    padding: 6px 0;
    border: none;
  }

  ::v-deep .v-data-table__mobile-row__header {
    font-weight: 600;
    color: rgba(0, 0, 0, 0.87);
    font-size: 0.75rem;
    text-transform: uppercase;
    flex: 0 0 100px;
  }

  ::v-deep .v-data-table__mobile-row__cell {
    text-align: right;
    flex: 1;
  }
}

.rounded-xl {
  border-radius: 16px !important;
}

.rounded-lg {
  border-radius: 12px !important;
}

@media (max-width: 599px) {
  .rounded-xl {
    border-radius: 12px !important;
  }
}

::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

@media print {
  .v-btn,
  .v-snackbar,
  .v-pagination,
  .v-overlay {
    display: none !important;
  }

  .njangi-app {
    padding: 0;
    background: white;
  }
}
</style>
